import { useEffect, useRef } from "react";

/**
 * Adds a magnetic pull to any button/anchor by translating it toward the cursor.
 */
export function useMagnetic(strength = 0.28, radius = 120) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const isTouch =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouch) return;

    let raf;
    const state = { tx: 0, ty: 0, cx: 0, cy: 0 };

    const move = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < radius) {
        state.tx = dx * strength;
        state.ty = dy * strength;
      } else {
        state.tx *= 0.8;
        state.ty *= 0.8;
      }
    };

    const leave = () => {
      state.tx = 0;
      state.ty = 0;
    };

    const tick = () => {
      state.cx += (state.tx - state.cx) * 0.18;
      state.cy += (state.ty - state.cy) * 0.18;
      el.style.transform = `translate3d(${state.cx}px, ${state.cy}px, 0)`;
      raf = requestAnimationFrame(tick);
    };

    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    window.addEventListener("mousemove", move);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mousemove", move);
      el.removeEventListener("mouseleave", leave);
      window.removeEventListener("mousemove", move);
    };
  }, [strength, radius]);

  return ref;
}
